import { type MediaKind } from '@aysar/contracts';
import { ImageOff, Loader2, Trash2, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SELECTION_ROW_ATTRIBUTE } from '@/shared/components/selection/useSelectionKeyboard/useSelectionKeyboard';
import { Button } from '@/shared/components/ui/Button/Button';
import { DialogBody } from '@/shared/components/ui/Dialog/Dialog';
import { useConfirm } from '@/shared/hooks/useConfirm/useConfirm';
import { cn } from '@/shared/utils/cn/cn.util';

import {
  MEDIA_ALPHA_BACKDROP_CLASSES,
  MEDIA_IMAGE_CLASSES,
  MEDIA_LIBRARY_FOOTER_CLASSES,
  MEDIA_LIBRARY_GRID_CLASSES,
  MEDIA_LIBRARY_MESSAGE_CLASSES,
  MEDIA_TILE_ACTION_CLASSES,
  MEDIA_TILE_CLASSES,
  MEDIA_TILE_SELECTED_CLASSES,
} from '../media.tokens';
import { type MediaSource } from '../types/media-source.types';

import { useMediaLibraryBodyLogic } from './useMediaLibraryBodyLogic';

export interface MediaLibraryBodyProps {
  kind: MediaKind;
  /** The current value, so its tile can say it is the chosen one. */
  value: string | null;
  onChoose: (mediaId: string) => void;
  source: MediaSource;
  /** Offered inside the dialog too, so an empty library is not a dead end. */
  onUploadRequested?: () => void;
}

/**
 * The library's contents — mounted **only while the dialog is open**.
 *
 * That is the whole reason it is a separate component: unmounting is what resets
 * the widened page, so reopening never lands the operator on a page they were
 * reading in a different session. The alternative is an effect watching `isOpen`
 * and calling `setState`, which this workspace rejects at error level and which
 * would be a worse answer even if it did not.
 *
 * Presentation only (U5): `useMediaLibraryBodyLogic` owns the paging, the
 * keyboard and the deletions.
 */
export function MediaLibraryBody({
  kind,
  value,
  onChoose,
  source,
  onUploadRequested,
}: MediaLibraryBodyProps) {
  const { t } = useTranslation('uiKit');
  const { confirm } = useConfirm();

  const {
    items,
    isFetching,
    isError,
    isEmpty,
    canLoadMore,
    canDelete,
    loadMore,
    retry,
    remove,
    activeValue,
    gridRef,
    onKeyDown,
  } = useMediaLibraryBodyLogic({ kind, source });

  return (
    <DialogBody>
      {isError ? (
        <div className={MEDIA_LIBRARY_MESSAGE_CLASSES}>
          <ImageOff aria-hidden className="h-6 w-6" />
          <p>{t('media.library.error')}</p>
          {retry === undefined ? null : (
            <Button type="button" variant="outline" size="sm" onClick={retry}>
              {t('media.retry')}
            </Button>
          )}
        </div>
      ) : isEmpty ? (
        <div className={MEDIA_LIBRARY_MESSAGE_CLASSES}>
          <ImageOff aria-hidden className="h-6 w-6" />
          <p>{t('media.library.empty')}</p>
          <p>{t('media.library.emptyHint')}</p>
          {onUploadRequested === undefined ? null : (
            <Button type="button" variant="outline" size="sm" onClick={onUploadRequested}>
              <Upload aria-hidden className="h-4 w-4" />
              {t('media.upload')}
            </Button>
          )}
        </div>
      ) : (
        <div
          ref={gridRef}
          role="group"
          aria-label={t('media.library.grid')}
          className={MEDIA_LIBRARY_GRID_CLASSES}
        >
          {items.map((item) => {
            const isChosen = item.id === value;
            const dimensions = t('media.dimensions', {
              width: item.width ?? 0,
              height: item.height ?? 0,
            });

            return (
              <div key={item.id} className="relative">
                <button
                  type="button"
                  // The attribute the roving stop finds rows by — a ref per tile
                  // would be a new identity every render and would detach every
                  // node on each page load.
                  {...{ [SELECTION_ROW_ATTRIBUTE]: item.id }}
                  tabIndex={activeValue === item.id ? 0 : -1}
                  aria-pressed={isChosen}
                  aria-label={`${isChosen ? t('media.library.chosen') : t('media.library.choose')} — ${dimensions}`}
                  className={cn(
                    MEDIA_TILE_CLASSES,
                    MEDIA_ALPHA_BACKDROP_CLASSES,
                    isChosen && MEDIA_TILE_SELECTED_CLASSES,
                    'w-full',
                  )}
                  // On the TILES rather than on the grid: the arrows move between
                  // buttons, and a `div` carrying key handlers is a control
                  // assistive tech cannot see (`jsx-a11y`, at error level here).
                  onKeyDown={onKeyDown}
                  onClick={() => {
                    onChoose(item.id);
                  }}
                >
                  <img src={item.url} alt="" className={MEDIA_IMAGE_CLASSES} />
                </button>

                {canDelete ? (
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon-sm"
                    aria-label={`${t('media.library.delete')} — ${dimensions}`}
                    className={MEDIA_TILE_ACTION_CLASSES}
                    onClick={() => {
                      void confirm({
                        title: t('media.library.deleteTitle'),
                        description: t('media.library.deleteBody'),
                        confirmLabel: t('media.library.deleteConfirm'),
                        tone: 'danger',
                        // Run INSIDE the dialog, so it holds and spins until the
                        // api answers. Closing first and deleting after leaves
                        // the operator not knowing whether it worked.
                        onConfirm: () => remove(item.id),
                      });
                    }}
                  >
                    <Trash2 aria-hidden className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {canLoadMore ? (
        <div className={MEDIA_LIBRARY_FOOTER_CLASSES}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isFetching}
            onClick={loadMore}
          >
            {isFetching ? (
              <>
                <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                {t('media.library.loading')}
              </>
            ) : (
              t('media.library.loadMore')
            )}
          </Button>
        </div>
      ) : null}
    </DialogBody>
  );
}
